import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import '../styles/CommunicationTimeline.css';

/**
 * InternalNoteForm Component
 * Standalone form for adding internal notes to a customer
 */
export function InternalNoteForm({ customerId, apiCall, onNoteAdded }) {
  const [noteContent, setNoteContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmitNote = async (e) => {
    e.preventDefault();

    if (!noteContent.trim()) {
      setSubmitError('Note content cannot be empty');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      await apiCall(`${API_BASE_URL}/customers/${customerId}/note`, {
        method: 'POST',
        body: JSON.stringify({ content: noteContent })
      });

      setNoteContent('');
      if (onNoteAdded) onNoteAdded();
    } catch (err) {
      setSubmitError('Failed to add note: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <h3 className="mb-0">Add Internal Note</h3>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmitNote}>
          <Form.Group>
            <Form.Control
              as="textarea"
              rows={3}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Add a note about this customer..."
              disabled={submitting}
            />
          </Form.Group>
          {submitError && (
            <Alert variant="danger" className="mt-2 mb-0">
              {submitError}
            </Alert>
          )}
          <Button
            type="submit"
            variant="primary"
            className="mt-2"
            disabled={submitting || !noteContent.trim()}
          >
            {submitting ? 'Adding Note...' : 'Add Note'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

/**
 * CommunicationTimeline Component
 * Displays all customer communications (SMS, Email, Notes) in chronological order
 * Task 32: Customer Detail Page Communication
 */
export function CommunicationTimeline({ customerId, apiCall, refreshKey }) {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('ALL'); // ALL, SMS, EMAIL, NOTE
  const [smsContent, setSmsContent] = useState('');
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [smsSuccess, setSmsSuccess] = useState(null);

  // Load communications
  const loadCommunications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall(`${API_BASE_URL}/customers/${customerId}/communication`);
      setCommunications(response?.data || []);
    } catch (err) {
      setError('Failed to load communications: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      loadCommunications();
    }
  }, [customerId, apiCall, refreshKey]);

  // Submit SMS
  const handleSendSms = async (e) => {
    e.preventDefault();

    if (!smsContent.trim()) {
      setSmsError('Message content cannot be empty');
      return;
    }

    try {
      setSmsSubmitting(true);
      setSmsError(null);
      setSmsSuccess(null);

      await apiCall(`${API_BASE_URL}/customers/${customerId}/sms`, {
        method: 'POST',
        body: JSON.stringify({ message: smsContent })
      });

      setSmsContent('');
      setSmsSuccess('SMS sent successfully');
      setTimeout(() => setSmsSuccess(null), 4000);
      await loadCommunications();
    } catch (err) {
      setSmsError('Failed to send SMS: ' + err.message);
    } finally {
      setSmsSubmitting(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Less than 1 minute ago
    if (diffMins < 1) {
      return 'Just now';
    }

    // Less than 1 hour ago
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }

    // Less than 24 hours ago
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }

    // Less than 7 days ago
    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }

    // More than 7 days ago - show full date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get type-specific styling
  const getTypeConfig = (type) => {
    switch (type) {
      case 'SMS':
        return {
          color: '#0d6efd',
          bgColor: '#cfe2ff',
          label: 'SMS',
          icon: '💬'
        };
      case 'EMAIL':
        return {
          color: '#198754',
          bgColor: '#d1e7dd',
          label: 'Email',
          icon: '📧'
        };
      case 'NOTE':
        return {
          color: '#6c757d',
          bgColor: '#e2e3e5',
          label: 'Note',
          icon: '📝'
        };
      default:
        return {
          color: '#6c757d',
          bgColor: '#e2e3e5',
          label: type,
          icon: '💬'
        };
    }
  };

  // Get direction indicator
  const getDirectionIndicator = (direction) => {
    switch (direction) {
      case 'INBOUND':
        return { icon: '⬇️', label: 'Inbound' };
      case 'OUTBOUND':
        return { icon: '⬆️', label: 'Outbound' };
      case 'INTERNAL':
        return { icon: '📝', label: 'Internal' };
      default:
        return { icon: '', label: direction };
    }
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="mt-2">Loading communications...</div>
        </Card.Body>
      </Card>
    );
  }

  // Filter communications based on selected type
  const filteredCommunications = filterType === 'ALL'
    ? communications
    : communications.filter(comm => comm.type === filterType);

  return (
    <Card className="communication-timeline-container">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Communication History</h3>
          <div className="btn-group" role="group">
            <Button
              size="sm"
              variant={filterType === 'ALL' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterType('ALL')}
            >
              All ({communications.length})
            </Button>
            <Button
              size="sm"
              variant={filterType === 'SMS' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterType('SMS')}
            >
              SMS ({communications.filter(c => c.type === 'SMS').length})
            </Button>
            <Button
              size="sm"
              variant={filterType === 'EMAIL' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterType('EMAIL')}
            >
              Email ({communications.filter(c => c.type === 'EMAIL').length})
            </Button>
            <Button
              size="sm"
              variant={filterType === 'NOTE' ? 'primary' : 'outline-primary'}
              onClick={() => setFilterType('NOTE')}
            >
              Notes ({communications.filter(c => c.type === 'NOTE').length})
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {/* Send SMS Form */}
        <Form onSubmit={handleSendSms} className="mb-4 mt-3">
          <Form.Group>
            <Form.Label>Send SMS</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={smsContent}
              onChange={(e) => setSmsContent(e.target.value)}
              placeholder="Type an SMS message to send to this customer..."
              disabled={smsSubmitting}
              maxLength={320}
            />
            <div className="sms-char-count">
              <small className={smsContent.length > 160 ? 'text-warning' : 'text-muted'}>
                {smsContent.length}/160
                {smsContent.length > 160 && ` (${Math.ceil(smsContent.length / 153)} segments)`}
              </small>
            </div>
          </Form.Group>
          {smsError && (
            <Alert variant="danger" className="mt-2 mb-0" dismissible onClose={() => setSmsError(null)}>
              {smsError}
            </Alert>
          )}
          {smsSuccess && (
            <Alert variant="success" className="mt-2 mb-0">
              {smsSuccess}
            </Alert>
          )}
          <Button
            type="submit"
            variant="success"
            className="mt-2"
            disabled={smsSubmitting || !smsContent.trim()}
          >
            {smsSubmitting ? 'Sending SMS...' : 'Send SMS'}
          </Button>
        </Form>

        <hr />

        {/* Timeline */}
        {error && <Alert variant="danger">{error}</Alert>}

        {communications.length === 0 ? (
          <div className="text-center text-muted py-4">
            No communications yet
          </div>
        ) : filteredCommunications.length === 0 ? (
          <div className="text-center text-muted py-4">
            No {filterType.toLowerCase()} communications found
          </div>
        ) : (
          <div className="communication-timeline">
            {filteredCommunications.map((comm) => {
              const typeConfig = getTypeConfig(comm.type);
              const directionInfo = getDirectionIndicator(comm.direction);

              return (
                <div
                  key={comm.id}
                  className={`communication-item communication-${comm.direction.toLowerCase()}`}
                >
                  <div className="communication-header">
                    <div className="communication-meta">
                      <Badge
                        bg="light"
                        text="dark"
                        style={{
                          backgroundColor: typeConfig.bgColor,
                          color: typeConfig.color,
                          marginRight: '6px'
                        }}
                      >
                        {typeConfig.icon} {typeConfig.label}
                      </Badge>
                      <Badge bg="secondary" className="me-2">
                        {directionInfo.icon} {directionInfo.label}
                      </Badge>
                      {comm.status && (
                        <Badge
                          bg={
                            comm.status === 'DELIVERED' ? 'success' :
                            comm.status === 'FAILED' ? 'danger' :
                            'warning'
                          }
                        >
                          {comm.status}
                        </Badge>
                      )}
                      {/* Add read/unread indicator for inbound messages */}
                      {comm.direction === 'INBOUND' && comm.type === 'SMS' && (
                        <Badge bg={comm.is_read ? 'secondary' : 'primary'} className="ms-2">
                          {comm.is_read ? 'Read' : 'Unread'}
                        </Badge>
                      )}
                    </div>
                    <div className="communication-timestamp">
                      {formatTimestamp(comm.created_at)}
                    </div>
                  </div>

                  <div
                    className="communication-content"
                    style={{
                      borderLeftColor: typeConfig.color
                    }}
                  >
                    {comm.content}
                  </div>

                  {comm.agent_name && (
                    <div className="communication-footer">
                      <small className="text-muted">
                        By: {comm.agent_name}
                      </small>
                    </div>
                  )}

                  {comm.external_id && (
                    <div className="communication-footer">
                      <small className="text-muted">
                        ID: {comm.external_id}
                      </small>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default CommunicationTimeline;
